import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/theme';

const ROWS_PER_CHUNK = 100;
const SCROLL_THRESHOLD = 0.9; // Load more when 90% scrolled

export default function PaginatedTable({
  rows,
  maxCols,
  onCellChange,
  editingCell,
  setEditingCell,
  selectedRange,
  setSelectedRange,
  isSelecting,
  setIsSelecting,
  editValue,
  setEditValue,
  tableRef,
  onCellMouseDown,
  onCellMouseOver,
  onCellDoubleClick,
  onCellFocus,
  isCellInRange,
  handleTableKeyDown,
  handleMouseUp,
  handleContextMenu,
  isSingleCellSelected,
  handleInputChange,
  handleInputBlur,
  handleInputKeyDown,
  hasConflict,
  conflictedCells,
  conflictMetadata,
}) {
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  const [renderedRowsCount, setRenderedRowsCount] = useState(ROWS_PER_CHUNK);
  const scrollContainerRef = useRef(null);
  const isLoadingMore = useRef(false);

  // Reset rendered rows when rows change (e.g., sheet switch)
  useEffect(() => {
    setRenderedRowsCount(ROWS_PER_CHUNK);
  }, [rows.length]);

  // Function to ensure a specific row is rendered (for keyboard navigation)
  const ensureRowVisible = useCallback((rowIndex) => {
    if (rowIndex >= renderedRowsCount && rowIndex < rows.length) {
      // Calculate how many rows to render (round up to next chunk)
      const neededCount = Math.min(
        Math.ceil((rowIndex + 1) / ROWS_PER_CHUNK) * ROWS_PER_CHUNK,
        rows.length
      );
      setRenderedRowsCount(neededCount);
    }
  }, [renderedRowsCount, rows.length]);

  // Handle scroll to load more rows
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoadingMore.current) return;

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Load more if scrolled near bottom and haven't rendered all rows
    if (scrollPercentage >= SCROLL_THRESHOLD && renderedRowsCount < rows.length) {
      isLoadingMore.current = true;
      // Load next chunk
      setRenderedRowsCount(prev => Math.min(prev + ROWS_PER_CHUNK, rows.length));
      
      // Reset loading flag after a brief delay
      setTimeout(() => {
        isLoadingMore.current = false;
      }, 100);
    }
  }, [renderedRowsCount, rows.length]);

  // Set up scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Merge refs: use both tableRef (from parent) and scrollContainerRef (for scroll handling)
  useEffect(() => {
    if (tableRef && scrollContainerRef.current) {
      // Store the scroll container ref in the parent's tableRef
      if (typeof tableRef === 'object' && tableRef !== null && 'current' in tableRef) {
        const scrollContainer = scrollContainerRef.current;
        // Store the DOM element and attach ensureRowVisible method
        // Delete existing property if it exists (for React StrictMode double render)
        if (scrollContainer.hasOwnProperty('ensureRowVisible')) {
          delete scrollContainer.ensureRowVisible;
        }
        // Define the property with configurable: true and writable: true so it can be updated
        Object.defineProperty(scrollContainer, 'ensureRowVisible', {
          value: ensureRowVisible,
          writable: true,
          enumerable: false,
          configurable: true,
        });
        tableRef.current = scrollContainer;
      }
    }
  }, [tableRef, ensureRowVisible]);

  // Get the visible rows slice
  const visibleRows = rows.slice(0, renderedRowsCount);

  return (
    <div>
      {/* Row Count Info */}
      <div style={{
        padding: '8px 0',
        marginBottom: 8,
        fontSize: 14,
        color: colors.textSecondary,
        transition: 'color 0.3s ease',
      }}>
        Showing {renderedRowsCount} of {rows.length} rows {renderedRowsCount < rows.length && '(scroll to load more)'}
      </div>

      {/* Scrollable Table Container */}
      <div
        ref={scrollContainerRef}
        tabIndex={0}
        onKeyDown={handleTableKeyDown}
        onMouseUp={handleMouseUp}
        onPaste={async (e) => {
          // This will be handled by the parent component's paste handler
          // We just need to prevent default and let the keyboard handler take over
          if (e.ctrlKey || e.metaKey) {
            // Let the keyboard handler process Ctrl+V
            return;
          }
        }}
        style={{
          outline: 'none',
          userSelect: isSingleCellSelected && editingCell ? 'auto' : 'none',
          height: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          overflowX: 'auto',
          border: `1px solid ${colors.border}`,
          borderRadius: 4,
          position: 'relative',
          backgroundColor: colors.background,
          transition: 'border-color 0.3s ease, background-color 0.3s ease',
          // Optimize scrolling performance
          willChange: 'scroll-position',
          transform: 'translateZ(0)', // Enable hardware acceleration
        }}
        onContextMenu={handleContextMenu}
      >
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            {visibleRows.map((row, i) => {
              // i is the index in visibleRows, which matches the actual row index in the full dataset
              // since we're slicing from the start
              const actualRowIndex = i;
              return (
                <tr key={actualRowIndex}>
                  {Array.from({ length: maxCols }).map((_, j) => {
                    const isSelected = isCellInRange(actualRowIndex, j, selectedRange);
                    const isEditing = editingCell && editingCell.row === actualRowIndex && editingCell.col === j;
                    const cellKey = `${actualRowIndex}-${j}`;
                    const hasConflictCell = conflictedCells?.has(cellKey);
                    const conflictInfo = conflictMetadata?.get(cellKey);
                    const cellValue = row[j] !== undefined ? row[j] : '';
                    
                    return (
                      <td
                        key={j}
                        data-cell={`${actualRowIndex},${j}`}
                        tabIndex={0}
                        style={{
                          border: isSelected ? `2px solid ${colors.primary}` : `1px solid ${colors.border}`,
                          padding: 6,
                          fontSize: 15,
                          maxWidth: 160,
                          maxHeight: 40,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          verticalAlign: 'middle',
                          cursor: 'pointer',
                          background: hasConflictCell ? (isDark ? '#7f1d1d' : '#fee2e2') : (isSelected ? (isDark ? '#1e3a8a' : '#e0e7ff') : colors.background),
                          title: hasConflictCell && conflictInfo ? `Konflikt: Izmenjeno od strane ${conflictInfo.editedBy?.name || 'drugog korisnika'} ${conflictInfo.editedAt ? `u ${new Date(conflictInfo.editedAt).toLocaleString()}` : ''}` : undefined,
                          color: colors.text,
                          outline: 'none',
                          userSelect: isEditing ? 'text' : 'none',
                          transition: 'border-color 0.2s, background-color 0.2s, color 0.2s',
                        }}
                        onMouseDown={e => {
                          if (e.button === 2) { e.preventDefault(); return; }
                          onCellMouseDown(actualRowIndex, j);
                        }}
                        onMouseOver={() => onCellMouseOver(actualRowIndex, j)}
                        onDoubleClick={() => onCellDoubleClick(actualRowIndex, j)}
                        onFocus={() => onCellFocus(actualRowIndex, j)}
                      >
                        {isEditing ? (
                          <input
                            type="text"
                            value={editValue}
                            autoFocus
                            style={{
                              width: '100%',
                              fontSize: 15,
                              border: 'none',
                              outline: 'none',
                              background: colors.background,
                              color: colors.text,
                              padding: 0,
                              margin: 0,
                              userSelect: 'text',
                              transition: 'background-color 0.2s, color 0.2s',
                            }}
                            onChange={handleInputChange}
                            onBlur={() => handleInputBlur(actualRowIndex, j)}
                            onKeyDown={e => handleInputKeyDown(e, actualRowIndex, j)}
                          />
                        ) : (
                          cellValue
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

